import type { Metadata } from 'next/types'
import { JURISDICTION_VALUES } from './jurisdictions'

export const CPC_SECTIONS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'Y',
] as const

export const LEGAL_STATUSES = {
  GRANTED: {
    value: 'granted',
    label: 'Granted',
    statuses: ['granted', 'active_reinstated'],
  },
  PENDING: {
    value: 'pending',
    label: 'Pending',
    statuses: ['pending'],
  },
  EXPIRED: {
    value: 'expired',
    label: 'Expired',
    statuses: [
      'expired',
      'expired_lifetime',
      'expired_fee_related',
      'expired_',
    ],
  },
  WITHDRAWN: {
    value: 'withdrawn',
    label: 'Withdrawn',
    statuses: ['withdrawn', 'revoked'],
  },
  ABANDONED: {
    value: 'abandoned',
    label: 'Abandoned',
    statuses: ['abandoned'],
  },
  CEASED: {
    value: 'ceased',
    label: 'Ceased',
    statuses: ['ceased'],
  },
  MISSING: {
    value: 'missing',
    label: 'Unknown',
    statuses: ['unknown'],
  },
}

export const LEGAL_STATUS = [
  'granted',
  'pending',
  'expired',
  'withdrawn',
  'abandoned',
  'missing',
  'ceased',
] as const

export const LEGAL_STATUS_LABELS = {
  granted: 'Granted',
  pending: 'Pending',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
  abandoned: 'Abandoned',
  ceased: 'Ceased',
  missing: 'Unknown',
} as const

export const AUTH_COOKIE_TOKEN = 'LegalicityCookie'
export const AUTH_COOKIE_CREATED = 'LegalicityCookieCreated'
export const AUTH_COOKIE_EXPIRY = 'LegalicityCookieExpiry'

export const CHECK_FOR_COOKIE_KEY = 'check_for_cookies'

export const HIGHLIGHT_COLOR_PALETTE = [
  '#FFCCCC',
  '#CCFFCC',
  '#CCCCFF',
  '#CCFFFF',
  '#FFCCFF',
  '#FFFFCC',
  '#FF9999',
  '#99FF99',
  '#9999FF',
  '#99FFFF',
  '#FF99FF',
  '#FFFF00',
  '#FF6666',
  '#55FF66',
  '#CC88BB',
  '#CCEE33',
] as const

interface TagColor {
  color: string
  textColor: string
  borderColor: string
}
export const TAG_COLOR_PALETTE: TagColor[] = [
  { color: '#FCA5A5', textColor: '#000000', borderColor: '#EF4444' },
  { color: '#FDBA74', textColor: '#000000', borderColor: '#F97316' },
  { color: '#FDE68A', textColor: '#000000', borderColor: '#F59E0B' },
  { color: '#A7F3D0', textColor: '#000000', borderColor: '#6EE7B7' },
  { color: '#6EE7B7', textColor: '#000000', borderColor: '#34D399' },
  { color: '#99F6E4', textColor: '#000000', borderColor: '#5EEAD4' },
  { color: '#A5F3FC', textColor: '#000000', borderColor: '#22D3EE' },
  { color: '#BFDBFE', textColor: '#000000', borderColor: '#60A5FA' },
  { color: '#C7D2FE', textColor: '#000000', borderColor: '#818CF8' },
  { color: '#DDD6FE', textColor: '#000000', borderColor: '#A78BFA' },
  { color: '#E9D5FF', textColor: '#000000', borderColor: '#C084FC' },
  { color: '#F5D0FE', textColor: '#000000', borderColor: '#E879F9' },
  { color: '#FBCFE8', textColor: '#000000', borderColor: '#F472B6' },
  { color: '#D1D5DB', textColor: '#000000', borderColor: '#9CA3AF' },
  { color: '#9CA3AF', textColor: '#FFFFFF', borderColor: '#6B7280' },
  { color: '#6B7280', textColor: '#FFFFFF', borderColor: '#4B5563' },
]

export const HIGHLIGHTED_CLASS_NAME = 'kw-highlighted'

export const METADATA_ICONS: Metadata['icons'] = {
  icon: [
    { url: '/icons/icon-128.png', sizes: '32x32' },
    { url: '/icons/icon-128.png', sizes: '16x16' },
  ],
  apple: [
    { url: '/icons/apple-icon-256.png', sizes: '256x256' },
    { url: '/icons/apple-icon-384.png', sizes: '384x384' },
    { url: '/icons/apple-icon-256.png', sizes: '256x256' },
    { url: '/icons/apple-icon-128.png', sizes: '128x128' },
  ],
  other: {
    rel: 'mask-icon',
    url: '/logo.svg',
    color: '#f19323',
  },
}

export const SUPPORT_EMAIL_ADDRESS = 'support@nlpatent.com'
export const SUPPORT_APP_FULL_URL = 'https://support.nlpatent.com'

export const PATENT_REGEX_PATTERNS = [
  /^\D{2,4}\d{5,15}\D{0,2}\d{0,3}$/,
  // Indian patent numbers are a bit different so if it's an indian patent we can be less strict on
  // the format.
  /^IN.{6,}/,
] as const

const JURISDICTION_REGEX_PART = `(?:${JURISDICTION_VALUES.join('|')})`
export const PATENT_NUMBER_GLOBAL_REGEX = new RegExp(
  `\\b(${JURISDICTION_REGEX_PART}\\d{5,15}[A-Z]{0,2}\\d{0,3})\\b`,
  'g'
)

export const MONITOR_TYPES = [
  'patent',
  'natural_language',
  'keyword',
  'filter_only',
  'office_action',
] as const

export const RELEVANCE_OPTIONS = ['low', 'medium', 'high', 'none'] as const

export const TYPEAHEAD_FIELD_TYPES = [
  'current_assignee',
  'applicant',
  'inventor',
] as const

export const FILTER_CPC_SECTION_OPTIONS = [
  {
    value: 'A',
    label: 'A | Human Necessities',
    description:
      'This includes agriculture; hunting and fishing; foodstuffs; tobacco and smoking devices; wearing apparel and accessories; furniture and household articles; medical sciences; medical devices; lifesaving and firefighting; sports, games, and entertainment.',
  },
  {
    value: 'B',
    label: 'B | Operations; Transporting',
    description:
      'This includes separating and mixing; chemical and physical processes; spraying, vibrating; turning; cleaning; waste disposal; metal-working; wood-working; working of clay/stone/cement; working of plastics; power and hand tools; milling; grinding and polishing; paper treatment and paper-making; printing; books and binding; writing articles; packing and storage; lifting and hauling; vehicles; railways; cycles; ships and water vessels; aviation; saddlery and upholstery; nanotechnology.',
  },
  {
    value: 'C',
    label: 'C | Chemistry; Metallurgy',
    description:
      'This includes organic and inorganic chemistry (compounds, compositions and processes); treatment of water; glass; cement, concrete and other stones; fertilizers; explosives; dyes and paints, polishes, resins and adhesives; petroleum, gas or coke industries; oils and fats; biochemistry and genetic engineering, microorganisms;  enzymes; saccharides; fermentation; beer, wine and spirits; metallurgy and alloys; electrolytic or electrophoretic processes and apparatuses; crystals.',
  },
  {
    value: 'D',
    label: 'D | Textiles; Paper',
    description:
      'This includes threads, yarn, ropes and other fibers; wrapping, spinning and twisting; weaving; sewing; treatment of textiles; paper-making; wall and floor materials; laundry.',
  },
  {
    value: 'E',
    label: 'E | Fixed Constructions',
    description:
      'This includes construction of roads, railways, bridges and platforms; hydraulic engineering; excavating; sewage and plumbing; building constructions; structures built for particular purposes (e.g., swimming); scaffolding; locks, keys and door fittings; drilling and mining.',
  },
  {
    value: 'F',
    label: 'F | Mechanical Engineering',
    description:
      'This includes engines and pumps; alternative energy; insulation; fastening or securing constructional elements; shafts; clutches and brakes; springs; belts, cables and ropes; gearing; pistons; pipes; safety devices; lighting; steam, burners and combustion; heating and ventilation; air-conditioning; refrigerators; furnaces; weapons, armour, and ammunition.',
  },
  {
    value: 'G',
    label: 'G | Physics',
    description:
      'This includes information storage; software; computing; data processing; image or video recognition; optics; spectacles and contact lenses; measuring, testing and analyzing; geophysics; meteorology; photography and cinematography; X-ray technology; photosensitive materials and processes; holographic processes or apparatuses; clocks and watches; ticket/fare issuing apparatuses; coin operated apparatuses; traffic control systems; educational or accessibility devices; circuits; musical instruments; acoustics; bioinformatics; IoT technology; nuclear physics.',
  },
  {
    value: 'H',
    label: 'H | Electricity',
    description:
      'This includes software; electric communication; transmission of digital information; wireless communication; basic electric elements; cables; conductors; semiconductors; batteries; insulators; magnets; transformers; capacitors; electric switches; electric incandescent lamps; antennas; lasers and radiation; electric circuits; electric motors, electric power generation, modulation, and amplification; coding and decoding; X-ray techniques.',
  },
  {
    value: 'Y',
    label: 'Y | Other',
    description:
      'Includes general tagging of new technological developments; general tagging of cross-sectional technologies spanning over several sections of the ipc; technical subjects covered by former uspc cross-reference art collections [xracs] and digests.',
  },
] as const

export const DATE_TOOLTIP =
  'Fill in either the From, To, or both the From and To fields to filter results by a date range.'

export const LICENSED_TOOLTIP = 'Data based on SEC filings.'
export const LITIGATED_TOOLTIP = 'US only. Based on US District Court records.'
export const PUBLICATION_TYPE_TOOLTIP =
  'In addition to the selected publication type the results will also include the "Unknown" and "Informational" publication types.'

export const MAXIMUM_CITATIONS_NUMBER = 20

export const DATE_FORMAT = 'MMM DD, YYYY'

export const MAX_PROJECTED_EXPIRATION_YEARS = 100

export const TAG_FILTER_KEY = 'tag_ids'
export const TITLE_SEARCH_KEY = 'query'
export const SORT_BY_KEY = 'order'
export const SORT_ORDER_KEY = 'order_direction'
export const DEFAULT_SORT_BY = 'updated_at'
export const ALLOWED_SORTING_ORDERS = ['asc', 'desc'] as const
export type SortingOrder = (typeof ALLOWED_SORTING_ORDERS)[number]
export const SORT_BY_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Creation Time' },
  { value: 'updated_at', label: 'Last Updated' },
] as const
export type SortByField = (typeof SORT_BY_OPTIONS)[number]['value']
export const DEFAULT_SORTING_ORDERS: Record<SortByField, SortingOrder> = {
  name: 'asc',
  created_at: 'desc',
  updated_at: 'desc',
}
